import { TranslationsContext } from '@teliaee/telia-translate';
import { observer } from 'mobx-react';
import React, { useContext } from 'react';
import {
  AnalyticsTrigger,
  Button,
  Card,
  CardContent,
  EqualHeight,
  Grid,
  GridColumn,
  Heading,
  Icon,
  ITagProps,
  List,
  ListItem,
  Separator,
  Slider,
  Spinner,
  Text,
  THeadingLevel
} from 'telia-front-react';
import GaPushOnMount from '../../content/GaPushOnMount';
import GaDataStore from '../../stores/GaDataStore';
import { PackageIds } from '../../stores/MobileLifeStore';
import { useStores } from '../../stores/RootStore';
import { twebTranslateOrEmpty } from '../../util/TranslateUtil';
import PrivateMobileCardPrice from '../PrivateMobileCardPrice';

export interface PrivateMobileCardOldProps {
  formName: string;
  packageName: string;
  buttonAriaLabel: string;
  singleUserDistributedPrice: number;
  singleUserDistributedCampaignPrice?: number;
  callsPackagePrice: number;
  multiUserDistributedPrice?: number;
  multiUserDistributedCampaignPrice?: number;
  multiUserRoamingAmount?: number;
  packageCustomDistributedPrices?: number[];
  packageCustomDistributedCampaignPrices?: number[];
  singleUserUrl: string;
  roamingAmount: number;
  packageIds: PackageIds;
  tagTop?: ITagProps | boolean;
  tag?: ITagProps;
  smallVolume?: boolean;
  hideBaltics?: boolean;
  forSingleUserOnly?: boolean;
  modalExtraContent?: React.ReactNode;
  campaignRulesLink?: React.ReactNode;
  headingLevel?: THeadingLevel;
}

const PrivateMobileCardOld = observer((props: PrivateMobileCardOldProps) => {
  const { languageStore, mobileLifeStore } = useStores();
  const { translate, translateOrEmpty } = useContext(TranslationsContext);
  const packageId = mobileLifeStore.getPackageId(props.packageIds);

  const getRoamingAmount = (): number => {
    const { multiUserRoamingAmount, roamingAmount } = props;
    return !!multiUserRoamingAmount && mobileLifeStore.selectedDeviceCount > 1 ? multiUserRoamingAmount : roamingAmount;
  };

  const handleModalButtonClick = () => {
    mobileLifeStore.setOrderModalOpen(true);
    mobileLifeStore.setSelectedMobilePackage(packageId);
  };

  const getTagTop = (): ITagProps | boolean => {
    if (mobileLifeStore.hasPackageDiscount(packageId)) {
      const internetPrices = mobileLifeStore.getPackagePrices(packageId);

      if (twebTranslateOrEmpty(translateOrEmpty, `mobile.package.discount.${internetPrices?.offerInfo?.offerName}`)) {
        return {
          text: translate(`mobile.package.discount.${internetPrices.offerInfo.offerName}`),
          purpose: 'campaign-3'
        };
      }
    }
    return props.tagTop ?? false;
  };

  const renderPackageTitle = (): JSX.Element => {
    const { packageName, headingLevel = 3 } = props;
    const packageTitle: string[] = packageName.split(' ');
    return (
      <Heading level={headingLevel} className="h4">
        {props.hideBaltics ? (
          <span className={languageStore.currentLanguage === 'ru' ? 'h3' : 'h2'}>{packageName}</span>
        ) : (
          <>
            <span className={languageStore.currentLanguage === 'ru' ? 'h3' : 'h2'}>{packageTitle[0]}</span> {packageTitle.length > 1 && packageTitle[1]}
          </>
        )}
      </Heading>
    );
  };

  const renderMobilePackageButton = (url?: string, onClick?: () => void): JSX.Element => (
    <Button
      text={translate('mobile.package.want.package')}
      url={url}
      block={true}
      onClick={onClick}
      iconRight="#arrow-right"
      type="expressive"
      ariaLabel={props.buttonAriaLabel}
    />
  );

  const renderSingleUserPackageInfo = (): JSX.Element => (
    <>
      <Card background="brand-secondary-bg-subtle">
        <CardContent>
          <Grid className="text-light text-small" align={['middle-xs']}>
            <GridColumn className="text-right" width={['xs-2']}>
              <Icon modifiers="#contact-lock" size="xs" />
            </GridColumn>
            <GridColumn className="text-left" width={['xs-10']}>
              <span>{translate('mobile.package.single.user')}</span>
            </GridColumn>
          </Grid>
        </CardContent>
      </Card>
      <Separator spacing="xsmall" color="invisible" />
    </>
  );

  const renderUserSlider = (): JSX.Element => (
    <>
      <Grid noWrap={true} align={['middle-xs']} horizontalGutter="small" verticalGutter="none">
        <GridColumn width={['min']}>
          <Icon modifiers="#people" size="xs" />
        </GridColumn>
        <GridColumn width={['min']} className="text-small">
          {translate('mobile.package.user.choose.amount')}
        </GridColumn>
      </Grid>
      <Slider
        id={`user-select-slider-${mobileLifeStore.selectedDeviceCount}`}
        className="text__margin-none"
        start={mobileLifeStore.selectedDeviceCount}
        onChange={(newValue) => {
          GaDataStore.pushWithoutCmsData({
            event: 'AutoEvent',
            eventCategory: 'Mobiilne elu',
            eventAction: 'Packages',
            eventLabel: `Slider - current-user-selected-slider-${newValue}`
          });
          mobileLifeStore.setSelectedDeviceCount(newValue as number, props.packageIds);
        }}
        range={{ min: 1, max: 5 }}
        step={1}
        pips={[1, 2, 3, 4, 5].map((sliderPip) => {
          return { value: sliderPip, label: sliderPip.toString() };
        })}
      />
    </>
  );

  return (
    <Card tag={props.tag} className="text-left" background="brand-secondary-bg-subtlest">
      <CardContent tagTop={getTagTop()}>
        <Text>
          <Separator spacing="xxsmall" color="invisible" />
          <EqualHeight group="mobile-package-title">
            {renderPackageTitle()}
            <p>{props.hideBaltics ? <>&nbsp;</> : translate('mobile.package.baltic')}</p>
          </EqualHeight>
          <EqualHeight group="user-slider" align="middle">
            {props.forSingleUserOnly ? renderSingleUserPackageInfo() : renderUserSlider()}
          </EqualHeight>
          {mobileLifeStore.isPricesFetched && mobileLifeStore.hasPackageDiscount(packageId) && (
            <GaPushOnMount pushWhenVisible={true} data={{ eventLabel: `Show - Discount - ${props.packageName}` }} />
          )}
          <EqualHeight group={'price'}>{mobileLifeStore.isPricesFetched ? <PrivateMobileCardPrice {...props} /> : <Spinner />}</EqualHeight>
          <EqualHeight group={'cta'}>
            {!props.forSingleUserOnly && mobileLifeStore.selectedDeviceCount > 1 ? (
              <AnalyticsTrigger trigger={{ callback: 'onClick', id: 'CTA - Form - Mobiilne Elu' }}>
                {renderMobilePackageButton(null, handleModalButtonClick)}
              </AnalyticsTrigger>
            ) : (
              <AnalyticsTrigger
                trigger={{
                  callback: 'onClick',
                  id: `CTA - ${props.packageName} mobiilipakett`
                }}
              >
                {renderMobilePackageButton(props.singleUserUrl)}
              </AnalyticsTrigger>
            )}
          </EqualHeight>
          <Separator spacing="small" weight="thin" />
          <List type="icon" size="large" iconPosition="middle" className="text-small text-left">
            <ListItem icon="#eu-et">
              {translate(`mobile.package.roaming${props.hideBaltics ? '.nonBaltic' : ''}`, { roamingAmount: getRoamingAmount() })}
            </ListItem>
            <ListItem icon="#mobile">{translate('mobile.package.modal.unlimited.calls')}</ListItem>
            <ListItem icon="#phone-message">{translate('mobile.package.calls.eu')}</ListItem>
          </List>
        </Text>
      </CardContent>
    </Card>
  );
});

export default PrivateMobileCardOld;
